export interface InputState {
  /** 0..1 */
  throttle: number;
  /** 0..1 */
  brake: number;
  /** -1..1, negative = left */
  steer: number;
  /** boolean: spacebar / gamepad A */
  handbrake: boolean;
  /** boolean: R key / gamepad Y — pulsed (true for one frame on press) */
  respawn: boolean;
  /** boolean: N key — pulsed (true for one frame on press) */
  toggleMinimapMode: boolean;
  /** boolean: V key / gamepad B (Circle) — pulsed (true for one frame on press) */
  cycleVehicle: boolean;
  /** boolean: M key — pulsed (true for one frame on press) */
  toggleMute: boolean;
  /** boolean: B key — pulsed; toggles the road-class debug HUD readout. */
  toggleRoadDebug: boolean;
  /** boolean: C key — pulsed (true for one frame on press) */
  toggleCamera: boolean;
  /** boolean: L key — pulsed (true for one frame on press) */
  toggleTimeOfDay: boolean;
  /** boolean: P key — pulsed (true for one frame on press). Toggles Photo mode. */
  togglePhotoMode: boolean;
}

const STEER_RAMP = 5.0; // 1/sec — how fast keyboard steer ramps to ±1
const STEER_DECAY = 8.0; // how fast it returns to 0

export class Input {
  private keys = new Set<string>();
  private steerSmoothed = 0;
  private respawnRequested = false;
  private prevRespawnButton = false;
  private prevMinimapKey = false;
  private prevCycleVehicleButton = false;
  private prevMuteKey = false;
  private prevRoadDebugKey = false;
  private prevCameraKey = false;
  private prevTimeKey = false;
  private prevPhotoKey = false;

  constructor() {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
  }

  /** Compute the current input state. Call once per frame. */
  poll(dt: number): InputState {
    const kThrottle =
      this.keys.has('KeyW') || this.keys.has('ArrowUp') ? 1 : 0;
    const kBrake =
      this.keys.has('KeyS') || this.keys.has('ArrowDown') ? 1 : 0;
    const kLeft = this.keys.has('KeyA') || this.keys.has('ArrowLeft') ? 1 : 0;
    const kRight = this.keys.has('KeyD') || this.keys.has('ArrowRight') ? 1 : 0;
    const kHandbrake = this.keys.has('Space');
    const kRespawn = this.keys.has('KeyR');

    const targetSteer = kRight - kLeft;

    // Gamepad overrides keyboard when it sends non-zero input.
    let gpThrottle = 0;
    let gpBrake = 0;
    let gpSteer: number | null = null;
    let gpHandbrake = false;
    let gpRespawn = false;
    let gpCycleVehicle = false;
    const pad = getActivePad();
    if (pad) {
      const lx = applyDeadzone(pad.axes[0] ?? 0, 0.15);
      if (lx !== 0) gpSteer = clamp(lx, -1, 1);
      // Right trigger
      const rt = pad.buttons[7]?.value ?? 0;
      const lt = pad.buttons[6]?.value ?? 0;
      gpThrottle = rt;
      gpBrake = lt;
      gpHandbrake = pad.buttons[0]?.pressed ?? false; // A / Cross
      gpRespawn = pad.buttons[3]?.pressed ?? false; // Y / Triangle
      gpCycleVehicle = pad.buttons[1]?.pressed ?? false; // B / Circle
    }

    const finalSteerTarget = gpSteer ?? targetSteer;

    // Smooth keyboard steer (gamepad is already analog; we still smooth for consistency).
    const towards = finalSteerTarget;
    const ramp = Math.abs(towards) > Math.abs(this.steerSmoothed) ? STEER_RAMP : STEER_DECAY;
    const t = 1 - Math.exp(-ramp * dt);
    this.steerSmoothed = this.steerSmoothed + (towards - this.steerSmoothed) * t;

    const respawnNow = kRespawn || gpRespawn;
    const respawnEdge = (respawnNow && !this.prevRespawnButton) || this.respawnRequested;
    this.respawnRequested = false;
    this.prevRespawnButton = respawnNow;

    const minimapNow = this.keys.has('KeyN');
    const minimapEdge = minimapNow && !this.prevMinimapKey;
    this.prevMinimapKey = minimapNow;

    const cycleNow = this.keys.has('KeyV') || gpCycleVehicle;
    const cycleEdge = cycleNow && !this.prevCycleVehicleButton;
    this.prevCycleVehicleButton = cycleNow;

    const muteNow = this.keys.has('KeyM');
    const muteEdge = muteNow && !this.prevMuteKey;
    this.prevMuteKey = muteNow;

    const roadDebugNow = this.keys.has('KeyB');
    const roadDebugEdge = roadDebugNow && !this.prevRoadDebugKey;
    this.prevRoadDebugKey = roadDebugNow;

    const cameraNow = this.keys.has('KeyC');
    const cameraEdge = cameraNow && !this.prevCameraKey;
    this.prevCameraKey = cameraNow;

    const timeNow = this.keys.has('KeyL');
    const timeEdge = timeNow && !this.prevTimeKey;
    this.prevTimeKey = timeNow;

    const photoNow = this.keys.has('KeyP');
    const photoEdge = photoNow && !this.prevPhotoKey;
    this.prevPhotoKey = photoNow;

    return {
      throttle: Math.max(kThrottle, gpThrottle),
      brake: Math.max(kBrake, gpBrake),
      steer: this.steerSmoothed,
      handbrake: kHandbrake || gpHandbrake,
      respawn: respawnEdge,
      toggleMinimapMode: minimapEdge,
      cycleVehicle: cycleEdge,
      toggleMute: muteEdge,
      toggleRoadDebug: roadDebugEdge,
      toggleCamera: cameraEdge,
      toggleTimeOfDay: timeEdge,
      togglePhotoMode: photoEdge,
    };
  }

  requestRespawn(): void {
    this.respawnRequested = true;
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    // Avoid swallowing typing inside text inputs.
    if (isTypingTarget(e.target)) return;
    this.keys.add(e.code);
    if (e.code === 'Space' || e.code.startsWith('Arrow')) e.preventDefault();
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    this.keys.delete(e.code);
  };

  private onBlur = (): void => {
    this.keys.clear();
  };
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;
}

function getActivePad(): Gamepad | null {
  if (typeof navigator === 'undefined' || !navigator.getGamepads) return null;
  const pads = navigator.getGamepads();
  for (const p of pads) {
    if (p && p.connected) return p;
  }
  return null;
}

function applyDeadzone(value: number, dead: number): number {
  if (Math.abs(value) < dead) return 0;
  const sign = Math.sign(value);
  return sign * ((Math.abs(value) - dead) / (1 - dead));
}

function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}
